import * as graphsApi from "@/src/api/graphs";
import {isPending, OP, trackPending} from "@/src/queries/pending";
import {getActivityList, getActivityName} from "@/lib/util/ModelUtil";

export const STEP_VALUES = {
    CREATE_SETUP: 1,
    DATAMODEL: 2,
    UPLOAD: 3,
    COPY_FROM: 4,
    SELECT_TEMPLATE: 5,
    MAP_ACTIVITIES: 6,
    IMPORT_BPMN: 7,
    CHOOSE_VARIANTS: 8,
    TEST_DRIVEN: 9,
    DISCOVERY_RESULT: 10,
}

const delay = (ms) => new Promise(resolve => setTimeout(resolve, ms));

const buildFieldErrors = (error) => {
    const err = error?.response?.data?.error;
    if (!err) {
        return { fieldErrors: {}, errorName: null };
    }
    if (err.field_errors) {
        return { fieldErrors: err.field_errors, errorName: err.name };
    }
    if (err.name === 'JOB_LICENSE_ERROR') {
        return {
            fieldErrors: {
                licence: 'You have reached the limit of graphs you can create. Please contact your administrator.'
            },
            errorName: err.name
        };
    }
    if (err.name) {
        return { fieldErrors: { [err.name]: err.message }, errorName: err.name };
    }
    return { fieldErrors: {}, errorName: null };
};
export default {
    namespaced: true,
    state: {
        name: '',
        selectedType: null,
        currentStep: STEP_VALUES.CREATE_SETUP,
        prevStep: STEP_VALUES.CREATE_SETUP,
        creatingElement: false,
        workspaceId: null,
        workspaceDatapoolId: null,
        // data source of the workspace the graph is created in, the graph's own overrides, the
        // workspace connector type and what the data source editor still misses (non-Celonis only)
        workspaceDatasource: null,
        datasource: null,
        connectorType: null,
        datasourceMissing: [],
        error: {},
        datamodelData: {
            datamodel_id: null,
            leading_object_table: '',
            pac_project_key: '',
            activity_table: ''
        },
        file: null,
        importActivities: null,
        importResult: null,
        mappedActivities: null,
        copyActivities: null,
        testDrivenTests: null,
        discoveryResult: null,
        datamodelConfig: null,
        groundTruthTraceIndex: 0,
        compareTraceIdxs: [],
        events: [],
        traces: [],
        positives: [],
        negatives: [],
        extractedRules: [],
        tempViewer: null,
        excludeRework: false,
    },
    mutations: {
        setName: (state, name) => {
            state.name = name;
        },
        setSelectedType: (state, type) => {
            state.selectedType = type;
        },
        setCurrentStep: (state, step) => {
            state.prevStep = state.currentStep;
            state.currentStep = step;
        },
        setCreatingElement: (state, value) => {
            state.creatingElement = value;
        },
        setWorkspaceId: (state, workspaceId) => {
            state.workspaceId = workspaceId;
        },
        setDatamodelData: (state, data) => {
            state.datamodelData = {
                ...state.datamodelData,
                ...data,
            };
        },
        setWorkspaceDatapoolId: (state, datapoolId) => {
            state.workspaceDatapoolId = datapoolId;
        },
        setWorkspaceDatasource: (state, datasource) => {
            state.workspaceDatasource = datasource;
        },
        setDatasource: (state, datasource) => {
            state.datasource = datasource;
        },
        setConnectorType: (state, connectorType) => {
            state.connectorType = connectorType;
        },
        setDatasourceMissing: (state, missing) => {
            state.datasourceMissing = missing || [];
        },
        setError: (state, error) => {
            state.error = error || {};
        },
        setFile: (state, file) => {
            state.file = file;
        },
        setImportActivities: (state, activities) => {
            state.importActivities = activities;
        },
        setImportResult: (state, result) => {
            state.importResult = result;
        },
        setMappedActivities: (state, mapped) => {
            state.mappedActivities = mapped;
        },
        setCopyActivities: (state, activities) => {
            state.copyActivities = activities;
        },
        setTestDrivenTests: (state, tests) => {
            state.testDrivenTests = tests;
        },
        setDiscoveryResult: (state, result) => {
            state.discoveryResult = result;
        },
        resetWizardState: (state, { datamodelId, workspaceDatapoolId, workspaceId, workspaceDatasource } = {}) => {
            state.name = '';
            state.selectedType = null;
            state.currentStep = STEP_VALUES.CREATE_SETUP;
            state.prevStep = STEP_VALUES.CREATE_SETUP;
            state.creatingElement = false;
            state.workspaceId = workspaceId ?? null;
            state.workspaceDatapoolId = workspaceDatapoolId ?? null;
            state.workspaceDatasource = workspaceDatasource ?? null;
            state.datasource = null;
            state.datasourceMissing = [];
            state.error = {};
            state.datamodelData = {
                datamodel_id: datamodelId ?? null,
                leading_object_table: '',
                pac_project_key: '',
                activity_table: ''
            };
            state.file = null;
            state.importActivities = null;
            state.importResult = null;
            state.mappedActivities = null;
            state.copyActivities = null;
            state.testDrivenTests = null;
            state.discoveryResult = null;
            state.tempViewer = null;
            state.excludeRework = false;
        },
        setTempViewer: (state, viewer) => {
            state.tempViewer = viewer;
        },
        setTraces: (state, traces) => {
            state.traces = traces;
        },
        setGroundTruthTraceIndex: (state, index) => {
            state.groundTruthTraceIndex = index;
        },
        setCompareTraceIdxs: (state, idxs) => {
            state.compareTraceIdxs = idxs;
        },
        setDatamodelConfig: (state, config) => {
            state.datamodelConfig = config;
        },
        setEvents: (state, events) => {
            state.events = events;
        },
        setPositives: (state, positives) => {
            state.positives = positives;
        },
        setNegatives: (state, negatives) => {
            state.negatives = negatives;
        },
        setExtractedRules: (state, rules) => {
            state.extractedRules = rules || [];
        },
        setAllowRework: (state, allow) => {
            state.excludeRework = allow;
        }
    },
    actions: {
        createGraph: async ({ state, rootGetters, dispatch, getters }, { workspaceId, definition } = {}) => {
            const resolvedWorkspaceId = workspaceId || state.workspaceId;
            const payload = {
                name: state.name,
                workspace_id: resolvedWorkspaceId
            };

            if (getters.filledFields && !getters.isCelonis) {
                payload.pac_project_key = state.datamodelData.pac_project_key.replace(/[^\w\d\s]/gi, '');
                if (state.datasource) {
                    payload.datasource = state.datasource;
                }
            } else if (getters.filledFields) {
                const datapoolId = state.datamodelData.datamodel_id
                    ? rootGetters['repoStore/findDatapool'](state.datamodelData.datamodel_id)
                    : null;
                payload.datapool_id = datapoolId;
                payload.pac_project_key = state.datamodelData.pac_project_key.replace(/[^\w\d\s]/gi, '');
                Object.assign(payload, state.datamodelData);
            }

            if (definition) {
                payload.definition = definition;
            }

            return dispatch('repoStore/addGraph', payload, { root: true });
        },
        startDiscovery: async ({ state, dispatch, commit, getters }) => {
            commit('setCurrentStep', STEP_VALUES.DISCOVERY_RESULT);
            try {
                const res = await trackPending(OP.START_DISCOVERY, () => graphsApi.startDiscovery({
                    datamodelConfig: getters.datamodelConfig,
                    groundTruthTrace: state.traces[state.groundTruthTraceIndex].trace,
                    compareTraces: state.compareTraceIdxs.map(idx => state.traces[idx].trace),
                    excludeRework: state.excludeRework,
                }));
                commit('setDiscoveryResult', res);
                return res;
            } catch (error) {
                commit('setCurrentStep', STEP_VALUES.DATAMODEL);
                return null;
            }
        },
        startTestDrivenDiscovery: async ({ state, dispatch, commit }) => {
            commit('setCurrentStep', STEP_VALUES.DISCOVERY_RESULT);
            const positives = state.testDrivenTests?.positives || [];
            const negatives = state.testDrivenTests?.negatives || [];
            const testsPayload = [];

            positives.forEach((t, idx) => {
                testsPayload.push({
                    name: `P${idx + 1}`,
                    type: 'positive',
                    trace: t.trace || [],
                    context: t.context || [],
                    require_accepting: true
                });
            });
            negatives.forEach((t, idx) => {
                testsPayload.push({
                    name: `N${idx + 1}`,
                    type: 'negative',
                    trace: t.trace || [],
                    context: t.context || [],
                    require_accepting: true
                });
            });

            try {
                const res = await trackPending(OP.START_TEST_DRIVEN_DISCOVERY, () => graphsApi.startTestDrivenDiscovery({
                    tests: testsPayload
                }));
                commit('setDiscoveryResult', res?.rules || res);
                return res;
            } catch (error) {
                commit('setCurrentStep', STEP_VALUES.TEST_DRIVEN);
                return null;
            }
        },
        createGraphWithDefinition: async ({ state, dispatch, commit }, { definition } = {}) => {
            if (state.creatingElement) {
                return { created: false };
            }
            commit('setError', {});
            commit('setCreatingElement', true);
            try {
                await dispatch('createGraph', { definition });
                return { created: true };
            } catch (error) {
                const { fieldErrors, errorName } = buildFieldErrors(error);
                commit('setError', fieldErrors);
                if (errorName === 'GRAPH_DUPLICATE_ATTRIBUTES') {
                    commit('setCurrentStep', STEP_VALUES.DATAMODEL);
                }
                return { created: false, error };
            } finally {
                commit('setCreatingElement', false);
            }
        },
        goToStep: async ({ commit }, step) => {
            commit('setCurrentStep', step);
        },
        handleBack: async ({ state, dispatch }) => {
            switch (state.currentStep) {
                case STEP_VALUES.DATAMODEL:
                    return dispatch('goToStep', STEP_VALUES.CREATE_SETUP);
                case STEP_VALUES.UPLOAD:
                case STEP_VALUES.COPY_FROM:
                case STEP_VALUES.SELECT_TEMPLATE:
                    return dispatch('goToStep', STEP_VALUES.DATAMODEL);
                case STEP_VALUES.MAP_ACTIVITIES:
                    if (state.selectedType === 'bpmn') {
                        return dispatch('goToStep', STEP_VALUES.UPLOAD);
                    }
                    if (state.selectedType === 'copy') {
                        return dispatch('goToStep', STEP_VALUES.COPY_FROM);
                    }
                    if (state.selectedType === 'template') {
                        return dispatch('goToStep', STEP_VALUES.SELECT_TEMPLATE);
                    }
                    return dispatch('goToStep', state.prevStep);
                case STEP_VALUES.IMPORT_BPMN:
                    return dispatch('goToStep', STEP_VALUES.MAP_ACTIVITIES);
                case STEP_VALUES.CHOOSE_VARIANTS:
                    return dispatch('goToStep', STEP_VALUES.DATAMODEL);
                case STEP_VALUES.DISCOVERY_RESULT:
                    return dispatch('goToStep', state.selectedType === 'discovery'
                        ? STEP_VALUES.CHOOSE_VARIANTS
                        : STEP_VALUES.TEST_DRIVEN);
                case STEP_VALUES.TEST_DRIVEN:
                    return dispatch('goToStep', STEP_VALUES.DATAMODEL);
                default:
                    return null;
            }
        },
        showBpmnImport: async ({ state, dispatch, commit }) => {
            try {
                commit('setImportResult', null);
                const formData = new FormData();
                formData.append('file', state.file);
                formData.append('activity_map', JSON.stringify(state.mappedActivities || {}));
                const res = await dispatch('graphStore/importBpmn', {
                    workspaceId: state.workspaceId,
                    formData
                }, { root: true });
                commit('setImportResult', res);
                await dispatch('goToStep', STEP_VALUES.IMPORT_BPMN);
                return res;
            } catch (error) {
                commit('setImportResult', null);
                return null;
            }
        },
        updateJsonAndCreate: async ({ state, rootState, commit, dispatch }) => {
            if (!state.tempViewer) {
                return { created: false };
            }
            const modeling = state.tempViewer.get('modeling');
            for (const a of getActivityList(state.tempViewer.get('canvas'))) {
                const activityName = getActivityName(a);
                if (state.mappedActivities && state.mappedActivities[activityName]) {
                    modeling.updateLabel(a, state.mappedActivities[activityName]);
                }
            }
            commit('graphStore/updateJson', null, { root: true });
            await delay(500);
            return dispatch('createGraphWithDefinition', {
                definition: rootState.graphStore.jsonData
            });
        },
        validateGraphAttributes: async ({ state, rootGetters, commit }) => {
            if (!state.datamodelData.datamodel_id) {
                return true;
            }
            const datapoolId = rootGetters['repoStore/findDatapool'](state.datamodelData.datamodel_id);
            const payload = {
                datamodel_id: state.datamodelData.datamodel_id,
                pac_project_key: state.datamodelData.pac_project_key?.replace(/[^\w\d\s]/gi, ''),
                leading_object_table: state.datamodelData.leading_object_table,
                datapool_id: datapoolId,
            };
            try {
                await trackPending(OP.VALIDATE_GRAPH_ATTRIBUTES, () => graphsApi.validateGraphAttributes(payload));
                commit('setError', {});
                return true;
            } catch (error) {
                const { fieldErrors } = buildFieldErrors(error);
                commit('setError', fieldErrors);
                return false;
            }
        },
        handleNext: async ({ state, rootState, dispatch }) => {
            switch (state.currentStep) {
                case STEP_VALUES.CREATE_SETUP:
                    return dispatch('goToStep', STEP_VALUES.DATAMODEL);
                case STEP_VALUES.DATAMODEL: {
                    if (state.datamodelData.datamodel_id) {
                        const valid = await dispatch('validateGraphAttributes');
                        if (!valid) return null;
                    }
                    if (state.selectedType === 'empty') {
                        return dispatch('createGraphWithDefinition');
                    }
                    if (state.selectedType === 'bpmn') {
                        return dispatch('goToStep', STEP_VALUES.UPLOAD);
                    }
                    if (state.selectedType === 'copy') {
                        return dispatch('goToStep', STEP_VALUES.COPY_FROM);
                    }
                    if (state.selectedType === 'template') {
                        return dispatch('goToStep', STEP_VALUES.SELECT_TEMPLATE);
                    }
                    if (state.selectedType === 'discovery') {
                        return dispatch('goToStep', STEP_VALUES.CHOOSE_VARIANTS);
                    }
                    if (state.selectedType === 'testDriven') {
                        return dispatch('goToStep', STEP_VALUES.TEST_DRIVEN);
                    }
                    return null;
                }
                case STEP_VALUES.UPLOAD:
                    return dispatch('goToStep', STEP_VALUES.MAP_ACTIVITIES);
                case STEP_VALUES.COPY_FROM:
                case STEP_VALUES.SELECT_TEMPLATE:
                    return dispatch('goToStep', STEP_VALUES.MAP_ACTIVITIES);
                case STEP_VALUES.MAP_ACTIVITIES:
                    if (state.selectedType === 'bpmn') {
                        return dispatch('showBpmnImport');
                    }
                    return dispatch('updateJsonAndCreate');
                case STEP_VALUES.IMPORT_BPMN:
                    return dispatch('createGraphWithDefinition', { definition: rootState.graphStore.jsonData });
                case STEP_VALUES.CHOOSE_VARIANTS:
                    return dispatch('startDiscovery');
                case STEP_VALUES.DISCOVERY_RESULT:
                    return dispatch('createGraphWithDefinition', { definition: rootState.graphStore.jsonData });
                case STEP_VALUES.TEST_DRIVEN:
                    return dispatch('startTestDrivenDiscovery');
                default:
                    return null;
            }
        },
        startItdmDiscovery: async ({rootState, commit}, payload) => {
            const res = await trackPending(OP.START_ITDM_DISCOVERY, () => graphsApi.startItdmDiscovery(payload));
            commit('setEvents', res.events);
            commit('setTraces', res.traces);
            commit('setPositives', res.positives);
            commit('setNegatives', res.negatives);
            return res
        },
    },
    getters: {
        name: (state) => state.name,
        selectedType: (state) => state.selectedType,
        currentStep: (state) => state.currentStep,
        prevStep: (state) => state.prevStep,
        creatingElement: (state) => state.creatingElement,
        workspaceId: (state) => state.workspaceId,
        workspaceDatapoolId: (state) => state.workspaceDatapoolId,
        error: (state) => state.error,
        datamodelData: (state) => state.datamodelData,
        file: (state) => state.file,
        importActivities: (state) => state.importActivities,
        importResult: (state) => state.importResult,
        mappedActivities: (state) => state.mappedActivities,
        copyActivities: (state) => state.copyActivities,
        testDrivenTests: (state) => state.testDrivenTests,
        discoveryResult: (state) => state.discoveryResult,
        datamodelConfig: (state, getters, rootState, rootGetters) => {
            const datamodelData = state.datamodelData || {};
            const { pac_project_key, ...rest } = datamodelData;
            const datapoolId = datamodelData.datamodel_id
                ? rootGetters['repoStore/findDatapool'](datamodelData.datamodel_id)
                : null;
            return {
                ...rest,
                datapool_id: datapoolId
            };
        },
        groundTruthTraceIndex: (state) => state.groundTruthTraceIndex,
        groundTruthTrace: (state) => state.traces[state.groundTruthTraceIndex]?.trace,
        compareTraceIdxs: (state) => state.compareTraceIdxs,
        traces: (state) => state.traces,
        events: (state) => state.events,
        positives: (state) => state.positives,
        negatives: (state) => state.negatives,
        extractedRules: (state) => state.extractedRules,
        tempViewer: (state) => state.tempViewer,
        excludeRework: (state) => state.excludeRework,
        // Celonis workspaces (or workspaces without a data connection) keep the data model step
        isCelonis: (state) => !state.connectorType || state.connectorType === 'celonis',
        workspaceDatasource: (state) => state.workspaceDatasource,
        datasource: (state) => state.datasource,
        filledFields: (state, getters) => (getters.isCelonis
            ? Boolean(
                state.datamodelData.leading_object_table &&
                state.datamodelData.pac_project_key &&
                state.datamodelData.datamodel_id &&
                state.datamodelData.activity_table
            )
            : Boolean(state.datamodelData.pac_project_key) && state.datasourceMissing.length === 0),
        hasTestDrivenTests: (state) => {
            const positives = state.testDrivenTests?.positives || [];
            const negatives = state.testDrivenTests?.negatives || [];
            return positives.length + negatives.length > 0;
        },
        footerButtons: (state, getters, rootState, rootGetters) => {
            const left = {
                visible: state.currentStep !== STEP_VALUES.CREATE_SETUP,
                label: 'Back',
                icon: 'pi pi-arrow-left',
                disabled: false
            };

            const right = {
                visible: true,
                label: 'Next',
                icon: 'pi pi-arrow-right',
                disabled: false
            };

            if (state.currentStep === STEP_VALUES.CREATE_SETUP) {
                right.disabled = state.name.trim() === '' || !state.selectedType;
            }

            if (state.currentStep === STEP_VALUES.DATAMODEL) {
                if (state.selectedType === 'empty') {
                    right.label = getters.filledFields ? 'Create' : 'Skip';
                    right.icon = 'pi pi-plus';
                } else if (['bpmn', 'copy', 'template'].includes(state.selectedType)) {
                    right.label = getters.filledFields ? 'Next' : 'Skip';
                } else if (state.selectedType === 'discovery') {
                    right.label = 'Next';
                    right.disabled = !getters.filledFields;
                } else if (state.selectedType === 'testDriven') {
                    right.label = 'Next';
                    right.disabled = !getters.filledFields;
                } else {
                    right.visible = false;
                }

                // DATA_API_TYPE.GET_DATAMODELS, has been excluded from loading as we can skip the step

                if (isPending(OP.VALIDATE_GRAPH_ATTRIBUTES) ||
                    isPending([OP.GET_DATAPOOLS, OP.GET_CASE_TABLES, OP.GET_ACTIVITY_TABLES])) {
                    right.disabled = true;
                    right.icon = 'pi pi-spin pi-spinner';
                }
            }

            if (state.currentStep === STEP_VALUES.UPLOAD) {
                right.visible = Boolean(state.importActivities && state.file);
            }

            if (state.currentStep === STEP_VALUES.COPY_FROM || state.currentStep === STEP_VALUES.SELECT_TEMPLATE) {
                right.visible = Boolean(state.copyActivities);
            }

            if (state.currentStep === STEP_VALUES.MAP_ACTIVITIES) {
                right.label = state.selectedType === 'bpmn' ? 'Next' : 'Create';
                right.icon = state.selectedType === 'bpmn' ? 'pi pi-arrow-right' : 'pi pi-plus';
            }

            if (state.currentStep === STEP_VALUES.IMPORT_BPMN) {
                right.label = 'Complete Import';
                right.icon = 'pi pi-plus';
                right.disabled = !state.importResult;
            }

            if (state.currentStep === STEP_VALUES.DISCOVERY_RESULT) {
                right.label = 'Create';
                right.icon = 'pi pi-plus';
                right.visible = state.extractedRules && state.extractedRules.length > 0;
            }

            if (state.currentStep === STEP_VALUES.TEST_DRIVEN) {
                right.disabled = !getters.hasTestDrivenTests;
            }

            if (state.currentStep === STEP_VALUES.CHOOSE_VARIANTS && isPending(OP.GET_TRACES)) {
           
                right.disabled = true;
                right.icon = 'pi pi-spin pi-spinner';
            }
            return { left, right };
        },
    }
}